import * as React from 'react';
import AddNewBarber from '../../pages/components/AddNewBarber';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbarContainer } from '@mui/x-data-grid';
import { ShopBarber } from '../../types/shop';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { useAuth0 } from '@auth0/auth0-react';
import { shopDeleteBarber } from '../../middleware/registration_service/deleteRequest';
import { roleContext } from '../../context/RoleContext';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { useProfileData } from '../../context/ProfileDataContext';

interface CustomToolbarProps {
  barberCount: number;
  barberSeatCount: number | null;
  hasSubscription: boolean;
}

const CustomToolbar = ({
  barberCount,
  barberSeatCount,
  hasSubscription,
}: CustomToolbarProps) => {
  const [show, setShow] = React.useState(false);
  const effectiveSeatCount = hasSubscription ? barberSeatCount : 0;
  const seatLimitReached =
    !hasSubscription ||
    effectiveSeatCount == null ||
    barberCount >= effectiveSeatCount;

  return (
    <GridToolbarContainer sx={{ width: '100%', flexDirection: 'column' }}>
      {!hasSubscription && (
        <Alert severity="warning" sx={{ width: '100%', mb: 1 }}>
          Please upgrade or activate a subscription to add barbers.
        </Alert>
      )}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 0.5,
        }}
      >
        {hasSubscription && effectiveSeatCount != null && (
          <Typography variant="body2" sx={{ color: '#666' }}>
            {barberCount} / {effectiveSeatCount} barber seats used
          </Typography>
        )}
        <Tooltip
          title={
            !hasSubscription
              ? 'Please upgrade or activate a subscription to add barbers.'
              : seatLimitReached
              ? 'You have reached the barber seat limit for your subscription. Upgrade your plan to add more barbers.'
              : undefined
          }
          disableHoverListener={!seatLimitReached}
          disableFocusListener={!seatLimitReached}
          disableTouchListener={!seatLimitReached}
        >
          <span>
            <Button
              className="buttonHover"
              variant="contained"
              disabled={seatLimitReached}
              sx={{
                p: 1.5,
                bgcolor: '#dd8709',
                m: '15px 0px',
                fontFamily: 'GFS Didot',
                fontWeight: 'bold',
                color: 'white',
                '&:hover': { bgcolor: '#b86e08' },
                '&.Mui-disabled': { bgcolor: '#e0b96a', color: '#fff' },
              }}
              onClick={() => setShow(!show)}
            >
              Add Barber
            </Button>
          </span>
        </Tooltip>
      </Box>
      {show && <AddNewBarber showOpen={show} onClose={() => setShow(false)} />}
    </GridToolbarContainer>
  );
};

export default function DataTable() {
  const { shopId, shop, setShop, partner, loading } = useProfileData();
  const { getAccessTokenSilently } = useAuth0();
  const shopBarbers: ShopBarber[] = shop?.shopBarbers ?? [];
  const [openDialog, setOpenDialog] = React.useState(false);
  const [selectedBarber, setSelectedBarber] = React.useState<bigint | null>(
    null
  );
  const role = React.useContext(roleContext);
  const { barberSeatCount } = useSubscriptionAccess(role);
  const hasSubscription = partner?.shopSubscriptionId != null;

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'First Name',
      description: 'This column has a value getter and is not sortable.',
      width: 250,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 250,
      description: 'This column has a value getter and is not sortable.',
    },
    { field: 'email', sortable: false, headerName: 'Email', width: 250 },
    {
      field: 'Delete',
      sortable: false,
      renderCell: (params) => (
        <Box
          className="buttonHover"
          sx={{ color: 'white', cursor: 'pointer' }}
          onClick={() => handleOpenDialog(params.row.id)}
        >
          <PersonRemoveIcon sx={{ color: '#c62828' }} />
        </Box>
      ),
    },
  ];

  const handleOpenDialog = (barberId: bigint) => {
    setSelectedBarber(barberId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBarber(null);
  };

  const handleConfirmDelete = async () => {
    if (shopId && selectedBarber !== null) {
      try {
        const token = await getAccessTokenSilently();
        await shopDeleteBarber(shopId, selectedBarber, token);
        setShop((prev) =>
          prev
            ? {
                ...prev,
                shopBarbers: (prev.shopBarbers ?? []).filter(
                  (barber) => barber.id !== selectedBarber
                ),
              }
            : prev
        );
        alert('Barber deleted successfully.');
      } catch (error) {
        console.error('Error deleting barber:', error);
        alert('Failed to delete barber. Please try again.');
      } finally {
        handleCloseDialog();
      }
    }
  };

  const paginationModel = { page: 0, pageSize: 5 }; // corrected property names

  return (
    <Box sx={{ h: 'auto', width: '100% !important', padding: ' 0px 0px' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <div style={{ height: 'auto', marginTop: '5px' }}>
          <DataGrid
            sx={{ marginBottom: '0 !important' }}
            rows={shopBarbers}
            columns={columns}
            slots={{ toolbar: CustomToolbar }}
            slotProps={{
              toolbar: {
                barberCount: shopBarbers.length,
                barberSeatCount,
                hasSubscription,
              } as CustomToolbarProps,
            }}
            pagination
            initialState={{ pagination: { paginationModel } }}
            pageSizeOptions={[5, 10]}
            disableRowSelectionOnClick={true}
          />
        </div>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle sx={{ fontFamily: 'GFS Didot', fontWeight: 'bold' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this barber?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              fontFamily: 'GFS Didot',
              fontWeight: 'bold',
              borderColor: '#333',
              color: '#333',
            }}
          >
            No
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            autoFocus
            sx={{ fontFamily: 'GFS Didot', fontWeight: 'bold' }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
